import type {TrpcUtils} from '@features/utils/trpc';
import {z} from 'zod';

/**
 * Invalidates every cached `getActiveStructure` entry belonging to `flowId`.
 * `refetchType: 'all'` repairs inactive entries poisoned by a version where
 * the screen didn't exist (frames use `refetchOnMount: false`, so a stale
 * inactive entry would otherwise render until a window refocus). The
 * predicate keeps the refetch burst scoped to this flow's screens —
 * component screen ids are flowId-prefixed, legacy UUID screen ids never
 * match and are unaffected.
 */
export function invalidateFlowActiveStructures(
  utils: TrpcUtils,
  flowId: string,
) {
  return utils.screens.getActiveStructure.invalidate(undefined, {
    refetchType: 'all',
    predicate: (query) => {
      const parsed = z
        .object({input: z.object({screenId: z.string()})})
        .safeParse(query.queryKey.at(1));
      return parsed.success && parsed.data.input.screenId.startsWith(flowId);
    },
  });
}

/**
 * Invalidates the flow's screen list, flow row, and EVERY cached
 * `getActiveStructure` query (unscoped — no `{screenId}` filter).
 *
 * Use ONLY for flow-wide mutations whose effect crosses every screen
 * (version change, recompile, full-flow reload). Every screen's active
 * structure is potentially affected, so refetching them all is correct.
 *
 * Do NOT use this from per-screen mutations — call
 * `utils.screens.getActiveStructure.invalidate({screenId})` instead,
 * otherwise all 200 screens on the canvas will refetch.
 */
export async function invalidateScreenCaches(utils: TrpcUtils, flowId: string) {
  await Promise.all([
    utils.screens.getFlowScreens.invalidate({flowId}),
    utils.flow.getFlow.invalidate({flowId}),
    utils.screens.getActiveStructure.invalidate(),
  ]);
}

/**
 * Components-flow variant: invalidates the flow's screen list and flow
 * row only. Component flows render from the compiled output snapshot on
 * `flowVersionsTable`, not from `getActiveStructure`, so that query is
 * intentionally not invalidated here.
 *
 * Same scoping rules as `invalidateScreenCaches`: this is a flow-wide
 * helper. Per-screen mutations should scope their own invalidations.
 */
export async function invalidateComponentsScreenCaches(
  utils: TrpcUtils,
  flowId: string,
) {
  await Promise.all([
    utils.screens.getFlowScreens.invalidate({flowId}),
    utils.flow.getFlow.invalidate({flowId}),
  ]);
}
